<template>
  <div class="grid-splitter" :class="{ 'flex-column': vertical }" ref="gridSplitter">
    <!--Horizontal-->
    <div v-if="!vertical" class="collapsed-area-left" v-show="vm.collapsed.left && vm.isVisible.left" @click="vm.collapsed.left = false">
      <div class="title">{{ leftOptions?.title }}</div>
      <div class="collapse-button">
        <i class="mdp-ChevronRight"></i>
      </div>
    </div>
    <div v-if="!vertical" class="leftArea" :class="leftOptions?.classes" ref="leftArea" v-show="!vm.collapsed.left && vm.isVisible.left" :style="areaStyle">
      <div class="right-collapse" v-show="!vm.collapsed.right" v-if="rightOptions?.allowCollapse" @click="vm.collapsed.right = true">
        <i class="mdp-ChevronRight"></i>
      </div>
      <slot name="left"></slot>
    </div>
    <div
      v-if="!vertical"
      class="rightArea"
      :class="rightOptions?.classes"
      ref="rightArea"
      v-show="!vm.collapsed.right && vm.isVisible.right"
      :style="areaStyle">
      <div class="left-collapse" v-show="!vm.collapsed.left" v-if="leftOptions?.allowCollapse" @click="vm.collapsed.left = true">
        <i class="mdp-ChevronLeft"></i>
      </div>
      <slot name="right"></slot>
    </div>
    <div v-if="!vertical" class="collapsed-area-right" v-show="vm.collapsed.right && vm.isVisible.right" @click="vm.collapsed.right = false">
      <div class="title">{{ rightOptions?.title }}</div>
      <div class="collapse-button">
        <i class="mdp-ChevronLeft"></i>
      </div>
    </div>

    <!--Vertical-->
    <div v-if="vertical" class="collapsed-area-top" v-show="vm.collapsed.top && vm.isVisible.top" @click="vm.collapsed.top = false">
      <div class="title">{{ topOptions?.title }}</div>
      <div class="collapse-button">
        <i class="mdp-ChevronBottom"></i>
      </div>
    </div>
    <div v-if="vertical" class="topArea" :class="topOptions?.classes" ref="topArea" v-show="!vm.collapsed.top && vm.isVisible.top" :style="areaStyle">
      <div class="bottom-collapse" v-show="!vm.collapsed.bottom" v-if="bottomOptions?.allowCollapse" @click="vm.collapsed.bottom = true">
        <i class="mdp-ChevronBottom"></i>
      </div>
      <slot name="top"></slot>
    </div>
    <div
      v-if="vertical"
      class="bottomArea"
      :class="bottomOptions?.classes"
      ref="bottomArea"
      v-show="!vm.collapsed.bottom && vm.isVisible.bottom"
      :style="areaStyle">
      <div class="top-collapse" v-show="!vm.collapsed.top" v-if="topOptions?.allowCollapse" @click="vm.collapsed.top = true">
        <i class="mdp-ChevronTop"></i>
      </div>
      <slot name="bottom"></slot>
    </div>
    <div v-if="vertical" class="collapsed-area-bottom" v-show="vm.collapsed.bottom && vm.isVisible.bottom" @click="vm.collapsed.bottom = false">
      <div class="title">{{ bottomOptions?.title }}</div>
      <div class="collapse-button">
        <i class="mdp-ChevronTop"></i>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Split from "split.js";
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { TranslateResult } from "vue-i18n";

//#region General
const gridSplitter = ref<HTMLElement>();
const leftArea = ref<HTMLElement>();
const rightArea = ref<HTMLElement>();
const topArea = ref<HTMLElement>();
const bottomArea = ref<HTMLElement>();

type AreaOptions = {
  title?: string | TranslateResult;
  allowCollapse?: boolean;
  startCollapsed?: boolean;
  isVisible?: boolean;
  classes?: string | string[] | Record<string, boolean>;
};

const props = defineProps<{
  isLeftCollapsed?: boolean; //For Sync Use
  isRightCollapsed?: boolean; //For Sync Use
  isTopCollapsed?: boolean; //For Sync Use
  isBottomCollapsed?: boolean; //For Sync Use
  isLeftVisible?: boolean; //For Sync Use
  isRightVisible?: boolean; //For Sync Use
  isTopVisible?: boolean; //For Sync Use
  isBottomVisible?: boolean; //For Sync Use
  topOptions?: AreaOptions;
  bottomOptions?: AreaOptions;
  leftOptions?: AreaOptions;
  rightOptions?: AreaOptions;
  vertical?: boolean;
  splitterOptions?: Split.Options;
  isGutterTransparent?: boolean;
}>();

const emits = defineEmits([
  "update:isLeftCollapsed",
  "update:isRightCollapsed",
  "update:isTopCollapsed",
  "update:isBottomCollapsed",
  "update:isLeftVisible",
  "update:isRightVisible",
  "update:isTopVisible",
  "update:isBottomVisible"
]);

let _split: Split.Instance | undefined;

let splitOptions = reactive<Split.Options>({
  sizes: [50, 50],
  minSize: 350,
  gutterSize: 10
});

const areaStyle = computed(() => {
  if (splitOptions && splitOptions.minSize) {
    return props.vertical ? { minHeight: `${splitOptions.minSize}px` } : { minWidth: `${splitOptions.minSize}px` };
  }
  return {};
});
//#endregion

//#region ViewModel
type ViewModel = {
  collapsed: { left: boolean; right: boolean; top: boolean; bottom: boolean };
  isVisible: { left: boolean; right: boolean; top: boolean; bottom: boolean };
};
const vm = reactive<ViewModel>({
  collapsed: {
    left: props.leftOptions?.startCollapsed ?? false,
    right: props.rightOptions?.startCollapsed ?? false,
    top: props.topOptions?.startCollapsed ?? false,
    bottom: props.bottomOptions?.startCollapsed ?? false
  },
  isVisible: {
    left: props.leftOptions?.isVisible ?? true,
    right: props.rightOptions?.isVisible ?? true,
    top: props.topOptions?.isVisible ?? true,
    bottom: props.bottomOptions?.isVisible ?? true
  }
});
//#endregion

//#region Splitter

const createSplitter = () => {
  if (props.splitterOptions) splitOptions = Object.assign(splitOptions, props.splitterOptions);

  if (!props.vertical && leftArea?.value && vm.isVisible.left && rightArea?.value && vm.isVisible.right && !(vm.collapsed.left || vm.collapsed.right)) {
    _split = Split([leftArea.value, rightArea.value], splitOptions);
  }

  if (props.vertical && topArea?.value && vm.isVisible.top && bottomArea?.value && vm.isVisible.bottom && !(vm.collapsed.top || vm.collapsed.bottom)) {
    Object.assign(splitOptions, { direction: "vertical" });
    _split = Split([topArea.value, bottomArea.value], splitOptions);
  }

  if (props.isGutterTransparent) {
    gridSplitter.value?.querySelector(".gutter")?.classList.add("transparent");
  }
};

const destroySplitter = () => {
  if (_split) {
    _split.destroy();
    _split = undefined;
  }
};

watch(
  () => props.splitterOptions,
  () => {
    destroySplitter();
    createSplitter();
  },
  { deep: true }
);

//#endregion

//#region Collapse

watch(
  vm.collapsed,
  (nv) => {
    if (nv.left || nv.right || nv.top || nv.bottom) {
      destroySplitter();
    } else {
      createSplitter();
    }
    emits("update:isLeftCollapsed", nv.left);
    emits("update:isRightCollapsed", nv.right);
    emits("update:isTopCollapsed", nv.top);
    emits("update:isBottomCollapsed", nv.bottom);
  },
  { deep: true }
);

watch(
  vm.isVisible,
  (nv) => {
    if (!nv.left || !nv.right || !nv.top || !nv.bottom) {
      destroySplitter();
    } else {
      createSplitter();
    }
    emits("update:isLeftVisible", nv.left);
    emits("update:isRightVisible", nv.right);
    emits("update:isTopVisible", nv.top);
    emits("update:isBottomVisible", nv.bottom);
  },
  { deep: true }
);

watch(
  () => props.isLeftCollapsed,
  (nv) => {
    vm.collapsed.left = nv;
  }
);
watch(
  () => props.isRightCollapsed,
  (nv) => {
    vm.collapsed.right = nv;
  }
);
watch(
  () => props.isTopCollapsed,
  (nv) => {
    vm.collapsed.top = nv;
  }
);
watch(
  () => props.isBottomCollapsed,
  (nv) => {
    vm.collapsed.bottom = nv;
  }
);
watch(
  () => props.isLeftVisible,
  (nv) => {
    vm.isVisible.left = nv;
  }
);
watch(
  () => props.isRightVisible,
  (nv) => {
    vm.isVisible.right = nv;
  }
);
watch(
  () => props.isTopVisible,
  (nv) => {
    vm.isVisible.top = nv;
  }
);
watch(
  () => props.isBottomVisible,
  (nv) => {
    vm.isVisible.bottom = nv;
  }
);

const collapse = (region: string, collapse = true) => {
  switch (region) {
    case "left":
      vm.collapsed.left = collapse;
      break;
    case "right":
      vm.collapsed.right = collapse;
      break;
    case "top":
      vm.collapsed.top = collapse;
      break;
    case "bottom":
      vm.collapsed.bottom = collapse;
      break;
  }
};

const isVisible = (region: string, visible = false) => {
  switch (region) {
    case "left":
      vm.isVisible.left = visible;
      break;
    case "right":
      vm.isVisible.right = visible;
      break;
    case "top":
      vm.isVisible.top = visible;
      break;
    case "bottom":
      vm.isVisible.bottom = visible;
      break;
  }
};
//#endregion

//#region Life Cycle
onMounted(() => {
  createSplitter();
});

onBeforeUnmount(() => {
  destroySplitter();
});
//#endregion

//#region Exposed
defineExpose({ collapse, isVisible });
//#endregion
</script>
